@if (state()?.isLoading) {
  <app-edit-form-skeleton />
} @else {
  <form class="flex flex-col gap-8" (submit.prevent)="onSubmit()" autocomplete="off">
    <div>
      <div class="flex items-center">
        <div class="flex flex-col flex-1">
          <h3 class="text-on-surface dark:text-on-surface-darkmode font-semibold">
            {{ 'cockpit.settings.general.title' | transloco }}
          </h3>
          <p class="text-sm text-on-surface-variant dark:text-on-surface-variant-darkmode">
            {{ 'cockpit.settings.general.description' | transloco }}
          </p>
        </div>
        <div class="flex-none flex gap-2 items-center">
          <ui-button
            class="hidden sm:inline"
            [loading]="isUpdateLoading()"
            [disabled]="isUpdateLoading() || !form().dirty()"
            type="submit"
          >
            <ng-icon name="heroCheck" size="20" slot="start" class="!block" />
            {{ 'cockpit.settings.general.saveButton' | transloco }}
          </ui-button>
        </div>
      </div>
      <div class="text-end">
        @if (showValidationErrors() && form().invalid()) {
          <ui-input-error class="block mt-8">
            {{ 'cockpit.settings.general.validationError' | transloco }}
          </ui-input-error>
        }

        <app-http-error [error]="updateError()" class="block mt-8 empty:hidden">
          {{ 'cockpit.settings.general.updateError' | transloco }}
        </app-http-error>
      </div>
    </div>

    <!-- profile picture -->
    <ui-input-label labelFor="">
      {{ 'cockpit.settings.general.profilePicture.label' | transloco }}
      <ui-file-input slot="input" accept="image/*" [formField]="form.profilePicture">{{
        'cockpit.settings.general.profilePicture.button' | transloco
      }}</ui-file-input>
      <app-field-errors slot="bottom" [fieldState]="form.profilePicture()" />
    </ui-input-label>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-8">
      <!-- name -->
      <ui-input-label>
        {{ 'cockpit.settings.general.form.name' | transloco }}
        <span class="text-error dark:text-error-darkmode">*</span>
        <ui-input-simple [formField]="form.name" />
        <app-field-errors slot="bottom" [fieldState]="form.name()" />
      </ui-input-label>

      <!-- hasDifferentLegalName -->
      <ui-toggle-label class="sm:col-span-2">
        <ui-toggle [formField]="form.hasDifferentLegalName" />
        {{ 'cockpit.settings.general.form.legalNameToggle' | transloco }}
      </ui-toggle-label>

      @if (form.hasDifferentLegalName().value()) {
        <!-- legalName -->
        <ui-input-label class="sm:col-span-2">
          {{ 'cockpit.organization.createForm.legalName.label' | transloco }}
          <span class="text-error dark:text-error-darkmode">*</span>
          <ui-input-simple [formField]="form.legalName" />
          <app-field-errors slot="bottom" [fieldState]="form.legalName()" />
        </ui-input-label>
      }

      <!-- personal consultant -->
      <ui-input-label *hasRole="['USER_ADMIN']">
        {{ 'cockpit.settings.general.form.personalConsultant' | transloco }}
        <span class="text-error dark:text-error-darkmode">*</span>
        <ui-select [formField]="form.personalConsultant">
          @for (pc of personalConsultantOptions; track pc.value) {
            <option [selected]="form.personalConsultant().value() === pc.value" [value]="pc.value">
              {{ pc.displayValue }}
            </option>
          }
        </ui-select>
        <app-field-errors slot="bottom" [fieldState]="form.personalConsultant()" />
      </ui-input-label>
      <!-- industry -->
      <ui-input-label class="col-start-1">
        {{ 'cockpit.settings.general.form.industry' | transloco }}
        <span class="text-error dark:text-error-darkmode">*</span>
        <ui-input-simple [formField]="form.industry" />
        <app-field-errors slot="bottom" [fieldState]="form.industry()" />
      </ui-input-label>
      <!-- size -->
      <ui-input-label>
        {{ 'cockpit.settings.general.form.size' | transloco }}
        <span class="text-error dark:text-error-darkmode">*</span>
        <ui-select [formField]="form.size">
          <option [selected]="form.size().value() === ''" value="">
            {{ 'cockpit.organization.createForm.size.placeholder' | transloco }}
          </option>
          <option [selected]="form.size().value() === 'MICRO'" value="MICRO">
            {{ 'cockpit.organization.createForm.size.options.micro' | transloco }}
          </option>
          <option [selected]="form.size().value() === 'SMALL'" value="SMALL">
            {{ 'cockpit.organization.createForm.size.options.small' | transloco }}
          </option>
          <option [selected]="form.size().value() === 'MEDIUM'" value="MEDIUM">
            {{ 'cockpit.organization.createForm.size.options.medium' | transloco }}
          </option>
          <option [selected]="form.size().value() === 'LARGE'" value="LARGE">
            {{ 'cockpit.organization.createForm.size.options.large' | transloco }}
          </option>
          <option [selected]="form.size().value() === 'ENTERPRISE'" value="ENTERPRISE">
            {{ 'cockpit.organization.createForm.size.options.enterprise' | transloco }}
          </option>
        </ui-select>
        <app-field-errors slot="bottom" [fieldState]="form.size()" />
      </ui-input-label>
    </div>

    <div *hasRole="['USER_ADMIN']">
      <ui-badge mode="dot" color="admin" class="block mb-2">
        <h3 class="font-semibold text-sm">
          {{ 'cockpit.settings.general.metadata.title' | transloco }}
        </h3>
      </ui-badge>

      <div class="grid grid-cols-1 md:grid-cols-2">
        <div class="py-1">{{ 'cockpit.settings.general.metadata.createdAt' | transloco }}</div>
        <div class="py-1 text-on-surface-variant dark:text-on-surface-variant-darkmode">
          {{ metadata()?.createdAt | date: 'dd.MM.yyyy HH:mm:ss' }}
        </div>
        <div class="py-1">{{ 'cockpit.settings.general.metadata.createdBy' | transloco }}</div>
        @if (metadata()?.createdBy; as createdBy) {
          <div class="py-1 text-on-surface-variant dark:text-on-surface-variant-darkmode">
            {{ createdBy.firstName }} {{ createdBy.lastName }} ({{ createdBy.email }})
          </div>
        } @else {
          <div class="py-1 text-on-surface-variant dark:text-on-surface-variant-darkmode">-</div>
        }
        <div class="py-1">{{ 'cockpit.settings.general.metadata.updatedAt' | transloco }}</div>
        <div class="py-1 text-on-surface-variant dark:text-on-surface-variant-darkmode">
          {{ metadata()?.updatedAt | date: 'dd.MM.yyyy HH:mm:ss' }}
        </div>
        <div class="py-1">{{ 'cockpit.settings.general.metadata.updatedBy' | transloco }}</div>
        @if (metadata()?.updatedBy; as updatedBy) {
          <div class="py-1 text-on-surface-variant dark:text-on-surface-variant-darkmode">
            {{ updatedBy.firstName }} {{ updatedBy.lastName }} ({{ updatedBy.email }})
          </div>
        } @else {
          <div class="py-1 text-on-surface-variant dark:text-on-surface-variant-darkmode">-</div>
        }
      </div>
    </div>

    <ui-button
      mode="admin"
      type="button"
      *hasRole="['USER_ADMIN']"
      (click)="onClickDelete()"
      [loading]="isDeleteLoading()"
    >
      <ng-icon name="heroTrash" size="20" class="!block" slot="start" />
      {{ 'cockpit.settings.general.deleteButton' | transloco }}
    </ui-button>

    <app-http-error [error]="deleteError()">
      {{ 'cockpit.settings.general.deleteError' | transloco }}
    </app-http-error>
  </form>
}
